import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { ChevronsUpDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import InstitutionGoToReviewButton from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsAnswers, useInstitutionDetailsMutation } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import type { InstitutionIncorporation, InstitutionIncorporationOrDefaults } from '@/features/institution/incorporation/schema';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { institutionDetailsIncorporationSchema } from '@/services/api/profile';
import { isJurisdictionPermitted } from '@/utils';

const InstitutionIncorporationFormForm = ({ defaultValues }: { defaultValues: InstitutionIncorporationOrDefaults }) => {
  const form = useForm<InstitutionIncorporation>({
    defaultValues,
    resolver: standardSchemaResolver(institutionDetailsIncorporationSchema),
  });
  const { isPending, mutate } = useInstitutionDetailsMutation();
  const { data: countries, countriesAsOptions } = useMainstreamCountries();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('incorporation');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionIncorporation, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { incorporation: data };

    mutate(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        const countryOfIncorporation = data.countryOfIncorporation;

        if (!isJurisdictionPermitted(countryOfIncorporation, countries ?? {})) {
          navigate(`${onboardingPath}/incorporation/forbidden-jurisdiction/${countryOfIncorporation}`, { replace: true });
        } else {
          navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
        }
      },
    });
  };

  return (
    <Form {...form}>
      <div className="flex flex-row justify-between -md:justify-end">
        <InstitutionGoToReviewButton onSubmit={() => form.handleSubmit((data) => onSubmit(data, { isReturningToReview: true }))()} />
      </div>
      <PageCard size="lg" bordered fixedFooter>
        <form name="institution-incorporation" onSubmit={form.handleSubmit((data) => onSubmit(data))}>
          <PageCardContent className="pt-8 space-y-6">
            <FormField
              control={form.control}
              name="countryOfIncorporation"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{t('institutionDetails.incorporation.countryOfIncorporation')}*</FormLabel>
                  <PopoverControlled>
                    {(_, setOpen) => (
                      <>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                              <PseudoInputLabel>
                                {field.value
                                  ? countriesAsOptions.find((option) => option.value === field.value)?.label
                                  : t('institutionDetails.incorporation.placeholders.countryOfIncorporation')}
                              </PseudoInputLabel>
                              <PseudoInputIcon>
                                <ChevronsUpDown />
                              </PseudoInputIcon>
                            </PseudoInputButton>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0" stretch>
                          <PseudoInputOptions
                            empty={t('statuses.noResults')}
                            options={countriesAsOptions}
                            placeholder={t('fundamentals.searchCountries')}
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(field.value === value ? defaultValues?.countryOfIncorporation : value);
                              setOpen(false);
                            }}
                          />
                        </PopoverContent>
                      </>
                    )}
                  </PopoverControlled>
                  <FormMessage />
                </FormItem>
              )}
            />
          </PageCardContent>
          <PageCardFooter>
            <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
              <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
              {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
            </Button>
          </PageCardFooter>
        </form>
      </PageCard>
    </Form>
  );
};
InstitutionIncorporationFormForm.displayName = 'InstitutionIncorporationFormForm';

const InstitutionIncorporationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'incorporation');
  const { isLoading: isLoadingCountries } = useMainstreamCountries();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow || isLoadingCountries;

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.incorporation ?? {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.incorporation.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.incorporation.description')}</PageHeadingDescription>
      </PageHeading>
      <InstitutionIncorporationFormForm defaultValues={defaultValues} />
    </section>
  );
};
InstitutionIncorporationForm.displayName = 'InstitutionIncorporationForm';

export default InstitutionIncorporationForm;
